/**
 * Turn persisted qualification reason CODES into one plain sentence.
 *
 * Why this exists
 * ---------------
 * The backend stores machine codes (`profitability_min_estimated_profit`,
 * `economics_fees_incomplete`, …). The Results grid rendered a card with a
 * plausible "est. profit" and NO reason, so a product the customer's own rules
 * rejected was visually indistinguishable from a winner. The codes were already
 * persisted; they simply never reached the card.
 *
 * Rules:
 *   * Never invent a reason and never guess a threshold value we were not given.
 *   * A missing value is INSUFFICIENT EVIDENCE, not "unprofitable" — those are
 *     different facts and the customer acts differently on each.
 *   * Only the first few reasons are surfaced; the full list stays on the detail
 *     popup so the card stays readable on mobile.
 */

const COPY = {
  // Profitability — the customer's own thresholds
  profitability_min_estimated_profit: "profit below your minimum",
  profitability_min_estimated_roi: "ROI below your minimum",
  profitability_min_margin: "margin below your minimum",
  profitability_min_gross_spread: "price spread below your minimum",
  profitability_min_gross_roi: "gross ROI below your minimum",
  profitability_max_acquisition_cost: "source cost above your maximum",
  negative_estimated_profit: "estimated loss",
  negative_spread: "Walmart price is below your source cost",
  economics_prices_unknown: "prices missing",
  economics_profit_unknown: "profit could not be computed",
  economics_fees_incomplete: "fees not fully evidenced",
  economics_missing_package_dimensions: "package dimensions needed",
  economics_missing_fulfillment_fee: "fulfillment fee needed",
  walmart_price_missing: "no Walmart price",
  source_price_missing: "no source price",
  acquisition_cost_unknown: "acquisition cost unknown",
  profitability_profit_unknown: "profit unknown",
  profitability_roi_unknown: "ROI unknown",
  profitability_margin_unknown: "margin unknown",
  // Identity
  no_walmart_match: "no Walmart match",
  identity_upstream_rejected: "match rejected upstream",
  identity_not_identifier_verified: "identity not identifier-verified",
  identity_insufficient_identity_evidence: "insufficient identity evidence",
  identity_brand_mismatch: "brand mismatch",
  identity_brand_conflict: "brand conflict",
  identity_pack_mismatch: "pack mismatch",
  identity_pack_differs: "pack differs",
  identity_rejected: "identity rejected",
  identity_unknown: "identity unknown",
  identity_identity_evidence_incomplete: "identity evidence incomplete",
  // Availability
  availability_unknown: "stock unknown",
  availability_stale: "stock evidence is stale",
  availability_source_unknown: "source stock unknown",
  availability_out_of_stock: "out of stock",
  // Demand / competition
  demand_signal_unknown: "demand evidence missing",
  competition_unknown: "competition evidence missing",
  qualification_not_evaluated: "not evaluated yet",
  missing_required_evidence: "required evidence missing",
  pack_equivalence: "pack equivalence unproven",
  referral_category_uncertain: "referral category uncertain",
};

const PREFIX_COPY = {
  "identity_": "identity",
  "availability_": "availability",
  "economics_": "economics",
  "profitability_": "profitability",
  "demand_": "demand",
  "competition_": "competition",
};

/** Readable phrase for one persisted reason code. */
export function reasonText(code) {
  const raw = String(code || "").trim();
  if (!raw) return "";
  const key = raw.split(":")[0];
  if (COPY[key]) return COPY[key];
  // `identity_pack_conflict:case_vs_unit` style — keep the qualifier readable.
  const suffix = raw.includes(":") ? raw.split(":").slice(1).join(":") : "";
  if (COPY[key] && suffix) return `${COPY[key]} (${suffix})`;
  for (const [prefix, label] of Object.entries(PREFIX_COPY)) {
    if (key.startsWith(prefix)) {
      return `${label}: ${key.slice(prefix.length).replace(/_/g, " ")}`;
    }
  }
  return key.replace(/_/g, " ");
}

/**
 * One-line verdict for a result card.
 *
 * Returns { state, text, reasons } where state is one of
 *   "qualifying" | "rejected" | "insufficient" | "unknown".
 */
export function qualificationVerdict(lead) {
  if (!lead) return { state: "unknown", text: "", reasons: [] };

  const reasons = Array.isArray(lead.disqualificationReasons)
    ? lead.disqualificationReasons.filter(Boolean)
    : [];
  const gaps = Array.isArray(lead.evidenceGaps) ? lead.evidenceGaps.filter(Boolean) : [];
  const qualification = String(lead.qualification || "").toUpperCase();

  // The persisted boolean is authoritative when present.
  if (lead.qualifies === true && !reasons.length) {
    return { state: "qualifying", text: "Meets all your requirements", reasons };
  }

  const insufficient =
    qualification === "INSUFFICIENT_EVIDENCE" || (gaps.length > 0 && !reasons.length);
  if (insufficient) {
    return {
      state: "insufficient",
      text: "Not enough evidence to qualify",
      reasons: [...reasons, ...gaps],
    };
  }

  if (lead.qualifies === false || reasons.length) {
    const shown = reasons.slice(0, 3).map(reasonText).filter(Boolean);
    return {
      state: "rejected",
      text: shown.length
        ? `Does not meet your requirements — ${shown.join(", ")}`
        : "Does not meet your requirements",
      reasons,
    };
  }

  // No verdict on the row (e.g. a research candidate with no evaluation).
  return { state: "unknown", text: "", reasons };
}

export default qualificationVerdict;
